import type { SearchSchemaInput } from '@tanstack/react-router';
import { parseEnum } from '@/shared/lib/listSearch';

/**
 * Which onboarding is open, and which tab.
 *
 * Both live in the URL for the same reason every list screen's filters do: a
 * colleague pasted a link to "Henrik's documents tab" should land there, and a
 * refresh should not throw the user back to the checklist.
 *
 * The tabs are a search param rather than five child routes. The repo's other
 * tabbed screen (`modules/settings`) uses real routes, and that is right there
 * because each settings tab is a genuinely separate page with its own data. An
 * onboarding's tabs are five views of **one** record, fetched in one request —
 * making them routes would add four route definitions that all load the same
 * query and differ only in which part of it they render.
 */
export const ONBOARDING_TABS = [
  'checklist',
  'profile',
  'identity',
  'contract',
  'documents',
] as const;

export type OnboardingTab = (typeof ONBOARDING_TABS)[number];

export interface OnboardingSearch {
  /** The onboarding being viewed. Absent means the empty state. */
  id?: string | undefined;
  tab: OnboardingTab;
}

export function validateOnboardingSearch(
  search: Partial<Record<keyof OnboardingSearch, unknown>> & SearchSchemaInput,
): OnboardingSearch {
  const id =
    typeof search.id === 'string' && search.id.trim().length > 0 ? search.id.trim() : undefined;

  return {
    id,
    // An unknown tab falls back rather than rendering nothing — a hand-edited
    // or stale link should still show a usable screen.
    tab: parseEnum(search.tab, ONBOARDING_TABS) ?? 'checklist',
  };
}
